/**
 * Google Ads conversion event definitions
 *
 * All conversions are tracked via GA4 Measurement Protocol (server-side),
 * then imported into Google Ads via GA4 linking.
 */

/**
 * Enum of all tracked Google Ads conversion events
 */
export enum GoogleAdsConversionEvent {
    EARLYPAY_FLOW_STARTED = 'Earlypay_Flow_Started',
    EARLYPAY_USER_VERIFIED = 'Earlypay_User_Verified',
    EARLYPAY_KYC_SUCCESS = 'Earlypay_KYC_Success',
    EARLYPAY_LINKED_ACCOUNT_ADDED = 'Earlypay_Linked_Account_Added',
    EARLYPAY_LINKED_DEBIT_CARD_ADDED = 'Earlypay_Linked_Debit_Card_Added',
    EARLYPAY_TRIAL_STARTED = 'Earlypay_Subscription_Confirmed',
    EARLYPAY_UNDERWRITING_APPROVED = 'Earlypay_Approved',
}

/**
 * Configuration for a conversion action
 */
export interface ConversionConfig {
    /** Track conversion value (e.g., loan amount) */
    trackValue?: boolean
    /** Default currency for value tracking */
    currency?: string
}

/**
 * Configuration for each conversion event
 */
export const CONVERSION_CONFIG: Record<
    GoogleAdsConversionEvent,
    ConversionConfig
> = {
    [GoogleAdsConversionEvent.EARLYPAY_FLOW_STARTED]: {},
    [GoogleAdsConversionEvent.EARLYPAY_USER_VERIFIED]: {},
    [GoogleAdsConversionEvent.EARLYPAY_KYC_SUCCESS]: {},
    [GoogleAdsConversionEvent.EARLYPAY_LINKED_ACCOUNT_ADDED]: {},
    [GoogleAdsConversionEvent.EARLYPAY_LINKED_DEBIT_CARD_ADDED]: {},
    [GoogleAdsConversionEvent.EARLYPAY_TRIAL_STARTED]: {},
    [GoogleAdsConversionEvent.EARLYPAY_UNDERWRITING_APPROVED]: {
        trackValue: true,
        currency: 'USD',
    },
}

// Backwards compatibility alias
export const CONVERSION_LABELS = CONVERSION_CONFIG

/**
 * Parameters for tracking a conversion
 */
export interface ConversionParams {
    /** Conversion value (e.g., loan amount) */
    value?: number
    /** Currency code (default: USD) */
    currency?: string
    /** Transaction ID for deduplication */
    transactionId?: string
    /** Event ID for cross-provider reconciliation (shared with AnalyticsService) */
    eventId?: string
}

/**
 * GA4 event name mapping for server-side tracking via Measurement Protocol.
 * These names are sent to GA4 and then imported into Google Ads.
 */
export const GA4_EVENT_NAMES: Record<GoogleAdsConversionEvent, string> = {
    [GoogleAdsConversionEvent.EARLYPAY_FLOW_STARTED]: 'earlypay_flow_started',
    [GoogleAdsConversionEvent.EARLYPAY_USER_VERIFIED]: 'earlypay_user_verified',
    [GoogleAdsConversionEvent.EARLYPAY_KYC_SUCCESS]: 'earlypay_kyc_success',
    [GoogleAdsConversionEvent.EARLYPAY_LINKED_ACCOUNT_ADDED]:
        'earlypay_linked_account_added',
    [GoogleAdsConversionEvent.EARLYPAY_LINKED_DEBIT_CARD_ADDED]:
        'earlypay_linked_debit_card_added',
    [GoogleAdsConversionEvent.EARLYPAY_TRIAL_STARTED]: 'earlypay_trial_started',
    [GoogleAdsConversionEvent.EARLYPAY_UNDERWRITING_APPROVED]:
        'earlypay_underwriting_approved',
}

/**
 * Gets the GA4 event name for a conversion event, with environment prefix.
 * - Production (prd): Uses unprefixed event names (e.g., 'earlypay_underwriting_approved')
 * - Other environments: Uses 'dev_' prefix (e.g., 'dev_earlypay_underwriting_approved')
 *
 * @param event - The conversion event to get the GA4 name for
 * @returns The GA4 event name with appropriate environment prefix
 */
export function getGA4EventName(event: GoogleAdsConversionEvent): string {
    const baseName = GA4_EVENT_NAMES[event]
    const isProduction = process.env.NEXT_PUBLIC_ENV === 'prd'
    return isProduction ? baseName : `dev_${baseName}`
}
